import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'preact-iso';
/**
 * Global navigation blocker registry.
 *
 * Allows a component to register a callback that is checked before
 * any route change — whether triggered programmatically (e.g. IntentActionTrigger)
 * or by the browser back/forward buttons (popstate).
 *
 * The blocker receives the target route string and should return:
 * - a URL string (the path to revert to) to block the navigation, or
 * - a falsy value to allow it.
 *
 * For programmatic navigation the caller only checks truthiness, so returning
 * a URL string works the same as returning `true`.
 *
 * @module NavigationBlocker
 * @memberof Mojito.Applications.Common
 */

let currentBlocker;

/**
 * Register a navigation blocker callback.
 *
 * @param {Function} blocker - Receives the target route string. Return the URL to revert once blocked, or undefined if allow.
 */
export const setBlocker = blocker => {
    currentBlocker = blocker;
};

/**
 * Retrieves a navigation blocker callback.
 *
 * @returns {Function} Blocker function. Return the URL to revert once blocked, or undefined if allow.
 */
export const getBlocker = () => currentBlocker;

/**
 * Remove the active navigation blocker.
 */
export const clearBlocker = () => {
    currentBlocker = undefined;
};

/**
 * Hook that blocks navigation (programmatic and browser back/forward) while active.
 *
 * Only one blocker can be active at a time — mounting a second instance
 * will overwrite the first.
 *
 * @param {boolean} shouldBlock - Enables blocking when `true`; clears the blocker when `false`.
 * @param {string} currentRoute - The URL to revert to (i.e. "stay here") when a navigation attempt is blocked.
 * @param {Function} isNavigatingAway - Predicate called with the target route path. Return `true` to block, `false` to allow.
 * @returns {Array} `[isBlocked, onAccept, onCancel]` — whether a navigation is blocked, confirm callback, cancel callback.
 *
 * @memberof Mojito.Applications.Common
 */
export const useNavigationBlocker = (shouldBlock, currentRoute, isNavigatingAway) => {
    const [targetRouteAttempt, setTargetRouteAttempt] = useState();
    const { route } = useLocation();

    useEffect(() => {
        if (!shouldBlock) {
            clearBlocker();
            return;
        }

        setBlocker(targetRoute => {
            if (isNavigatingAway(targetRoute)) {
                setTargetRouteAttempt(targetRoute);
                return currentRoute;
            }
        });

        return () => clearBlocker();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shouldBlock, currentRoute]);

    const onAccept = useCallback(() => {
        if (targetRouteAttempt) {
            setTargetRouteAttempt(undefined);
            clearBlocker();
            route(targetRouteAttempt);
        }
    }, [targetRouteAttempt, route]);

    const onCancel = useCallback(() => {
        setTargetRouteAttempt(undefined);
    }, []);

    return [!!targetRouteAttempt, onAccept, onCancel];
};

// Intercept browser back/forward before Mojito LocationProvider handles
// the popstate event. Registered at module scope so it is guaranteed to be
// in the listener list before LocationProvider's useLayoutEffect adds its own.
if (typeof window !== 'undefined') {
    window.addEventListener(
        'popstate',
        e => {
            if (!currentBlocker) return;

            const targetPath = window.location.pathname + window.location.search;
            const revertUrl = currentBlocker(targetPath);

            if (revertUrl) {
                e.stopImmediatePropagation();
                history.pushState(null, '', revertUrl);
            }
        },
        true
    );
}
